import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Ticket, Building2, Clock, Image, Timer, RefreshCw } from 'lucide-react';
import type { Offer } from '../../types';

interface OfferDetailsProps {
  offer: Offer;
}

export function OfferDetails({ offer }: OfferDetailsProps) {
  const isExpired = new Date(offer.expires_at) < new Date();
  
  return (
    <div className="space-y-3">
      {/* Offer Header with Status */}
      <div className="flex justify-end gap-2">
        <Badge 
          variant={offer.is_active ? 'default' : 'secondary'}
          className="px-2 py-0.5 text-xs"
        >
          {offer.is_active ? 'Active' : 'Inactive'}
        </Badge>
        {isExpired && (
          <Badge variant="destructive" className="px-2 py-0.5 text-xs">
            Expired
          </Badge>
        )}
      </div>

      {/* Media */}
      {offer.media_url && (
        <Card>
          <CardHeader className="pb-1 pt-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Image className="h-3.5 w-3.5 text-muted-foreground" />
              Media
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-2">
            <div className="max-w-md mx-auto">
              <img 
                src={offer.media_url} 
                alt="Offer media" 
                className="w-full rounded shadow-sm hover:shadow-md transition-shadow"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Venue Information */}
      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            Venue Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pb-2">
          <div className="flex justify-between items-center py-1 border-b">
            <span className="text-xs text-muted-foreground">Venue</span>
            <span className="text-sm font-medium">{offer.venue_name}</span>
          </div>
          <div className="flex justify-between items-center py-1">
            <span className="text-xs text-muted-foreground">Organization</span>
            <span className="text-sm font-medium">{offer.organization_name}</span>
          </div>
        </CardContent>
      </Card>

      {/* Offer Details */}
      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Ticket className="h-3.5 w-3.5 text-muted-foreground" />
            Offer Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pb-2">
          <div className="py-1">
            <p className="text-xs text-muted-foreground mb-1">Short Description</p>
            <p className="text-sm font-medium">{offer.short_description}</p>
          </div>
          {offer.detailed_description && (
            <div className="py-1 border-t">
              <p className="text-xs text-muted-foreground mb-1">Detailed Description</p>
              <p className="text-xs whitespace-pre-wrap">{offer.detailed_description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Claim Statistics */}
      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            Claim Statistics
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-blue-50 dark:bg-blue-950/30 p-2 rounded border border-blue-200 dark:border-blue-900">
              <p className="text-[10px] font-medium text-blue-600 dark:text-blue-400 mb-0.5">
                Total Claims
              </p>
              <p className="text-lg font-bold">{offer.total_claims}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-950/30 p-2 rounded border border-green-200 dark:border-green-900">
              <p className="text-[10px] font-medium text-green-600 dark:text-green-400 mb-0.5">
                Active
              </p>
              <p className="text-lg font-bold">{offer.active_claims}</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-950/30 p-2 rounded border border-purple-200 dark:border-purple-900">
              <p className="text-[10px] font-medium text-purple-600 dark:text-purple-400 mb-0.5">
                Redeemed
              </p>
              <p className="text-lg font-bold">{offer.redeemed_claims}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Claim Settings */}
      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
            Claim Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pb-2">
          <div className="flex justify-between items-center py-1 border-b">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Timer className="h-3 w-3" />
              Claim Limit
            </span>
            <Badge variant="outline" className="font-mono text-xs px-2 py-0">
              {offer.claim_frequency} {offer.claim_frequency === 1 ? 'claim' : 'claims'}
            </Badge>
          </div>
          <div className="flex justify-between items-center py-1">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Farming Time
            </span>
            <Badge variant="outline" className="font-mono text-xs px-2 py-0">
              {offer.farming_time_in_hours} {offer.farming_time_in_hours === 1 ? 'hour' : 'hours'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Timestamps */}
      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            Timestamps
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pb-2">
          <div className="flex justify-between items-center py-1 border-b">
            <span className="text-xs text-muted-foreground">Created</span>
            <span className="text-xs font-medium">
              {format(new Date(offer.created_at), 'PP')}
            </span>
          </div>
          <div className="flex justify-between items-center py-1 border-b">
            <span className="text-xs text-muted-foreground">Last Updated</span>
            <span className="text-xs font-medium">
              {format(new Date(offer.updated_at), 'PP')}
            </span>
          </div>
          <div className="flex justify-between items-center py-1">
            <span className="text-xs text-muted-foreground">Expires At</span>
            <span className={`text-xs font-medium ${isExpired ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
              {format(new Date(offer.expires_at), 'PPpp')}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}